import { api, del, get, getPaginated, patch, post } from "./api-client";
import { newIdempotencyKey } from "./idempotency";

import type {
  ReferralTree, ReferralSummary, ReferralCommissionTransaction,
  ReferralRuleVersion,
} from "@/types/referral";
import type {
  StarStatus, StarOffer, StarOfferProgress, StarOfferClaim,
  StarQualificationRule,
} from "@/types/star";
import type {
  Agent, AgentTransaction, AgentUserSearchResult,
} from "@/types/agent";
import type {
  ReportOverview, ReportPeriod, SalesReport, TopUsersReport,
  UserPerformanceReport,
} from "@/types/report";
import type {
  SpecialOffer, SpecialOfferPayout,
} from "@/types/special-offer";

import type { HomepageSection, StorefrontData, AdvancedDashboardStats, HeroSliderItem } from "@/types/storefront";


// ─────────────────────────────────────────── Referral (Volume 11)
export const referralApi = {
  tree:        (generation?: number) =>
    get<ReferralTree>("/referrals/tree",
                       generation ? { generation } : {}),
  summary:     () => get<ReferralSummary>("/referrals/summary"),
  commissions: () => get<ReferralCommissionTransaction[]>("/referrals/commissions"),

  admin: {
    getConfig: () => get<ReferralRuleVersion | null>("/admin/referrals/configuration"),
    publishConfig: (rate: string) =>
      post<ReferralRuleVersion>(
        "/admin/referrals/configuration", { rate },
        { idempotencyKey: newIdempotencyKey() },
      ),
    configHistory: () =>
      get<ReferralRuleVersion[]>("/admin/referrals/configuration/history"),
    transactions: (params?: { user_id?: string }) =>
      get<ReferralCommissionTransaction[]>("/admin/referrals/commissions", params),
  },
};

// ─────────────────────────────────────────── Star (Volume 12)
export const starApi = {
  status:       () => get<StarStatus>("/star/status"),
  offers:       () => get<StarOffer[]>("/star/offers"),
  offerProgress: (offerId: number) =>
    get<StarOfferProgress>(`/star/offers/${offerId}/progress`),
  claims:       () => get<StarOfferClaim[]>("/star/claims"),

  admin: {
    getConfig: () => get<StarQualificationRule | null>("/admin/star/configuration"),
    updateOffer: (id: number, payload: Partial<StarOffer>) =>
      patch<{ success: boolean }>(`/admin/star/offers/${id}/update`, payload),
    publishConfig: (payload: {
      min_own_points: number;
      min_team_points: number;
      own_only_threshold: number;
      max_team_depth: number;
    }) => post<StarQualificationRule>(
      "/admin/star/configuration", payload,
      { idempotencyKey: newIdempotencyKey() },
    ),
    offers: () => get<StarOffer[]>("/admin/star/offers"),
    createOffer: (payload: {
      name: string;
      description: string;
      period_type: string;
      own_point_target: number;
      combined_point_target: number;
      bonus_amount: string;
    }) => post<StarOffer>(
      "/admin/star/offers", payload,
      { idempotencyKey: newIdempotencyKey() },
    ),
    forceStarPointReset: () => post<{ success: boolean; message: string }>("/admin/star/force-reset-points"),
  },
};


// ─────────────────────────────────────────── Reward Rules (For Customers)
export const rewardApi = {
  rules: () => get<any[]>("/admin/reward-rules"), // Reusing the admin endpoint for read-only rule display
};

// ─────────────────────────────────────────── Agent (Volume 13)
export const agentApi = {
  checkStatus: () => get<{ is_agent: boolean; status: string; shop_name: string }>("/agent/check-status"),
  profile:   () => get<Agent>("/agent/profile"),
  publicVendors: () => get<Agent[]>("/public/vendors"), // 🛠️ NEW
  searchUser: (userId: string) => get<AgentUserSearchResult>("/agent/users/search", { user_id: userId }),
  distribute: (payload: any) => post<AgentTransaction>("/agent/points/distribute", payload, { idempotencyKey: newIdempotencyKey() }),
  history:    (userId?: string) => get<AgentTransaction[]>("/agent/distributions", userId ? { user_id: userId } : {}),
  
  // 🛠️ NEW: Vendor Methods
  updateShop: (payload: any) => patch<Agent>("/agent/profile/update", payload), // Assumes you'd add this simple patch route in backend
  vendorOrders: () => get<any[]>("/agent/vendor/orders"),
  updateOrderStatus: (orderId: string, status: string) => patch<{ success: boolean }>(`/agent/vendor/orders/${orderId}/status`, { status }),
  suggestPoints: (amount: number) => get<{ suggested_points: number }>("/agent/points/suggest", { amount }),

  admin: {
    list: () => get<Agent[]>("/admin/agents"),
    assign: (userId: string) =>
      post<Agent>("/admin/agents/assign", { user_id: userId },
                    { idempotencyKey: newIdempotencyKey() }),
    loadPoints: (agentId: number, points: number, reference: string) =>
      post<AgentTransaction>(
        `/admin/agents/${agentId}/load-points`,
        { points, reference },
        { idempotencyKey: newIdempotencyKey() },
      ),
    setStatus: (agentId: number, status: string) =>
      post<Agent>(`/admin/agents/${agentId}/status`, { status },
                    { idempotencyKey: newIdempotencyKey() }),
    history: (agentId: number) =>
      get<AgentTransaction[]>(`/admin/agents/${agentId}/history`),
  },
};

// ─────────────────────────────────────────── Report (Volume 14)
export const reportApi = {
  overview: (params: { period: ReportPeriod; date_from?: string; date_to?: string }) =>
    get<ReportOverview>("/admin/reports/overview", params),
  sales: (params: { period: ReportPeriod; date_from?: string; date_to?: string }) =>
    get<SalesReport>("/admin/reports/sales", params),
  topUsers: (params: { period: ReportPeriod; date_from?: string; date_to?: string; limit?: number }) =>
    get<TopUsersReport>("/admin/reports/top-users", params),
  userPerformance: (params: { user_id: string; period: ReportPeriod; date_from?: string; date_to?: string }) =>
    get<UserPerformanceReport>("/admin/reports/user-performance", params),
  pdfUrl: (params: { type: "sales" | "top_users"; period: ReportPeriod; date_from?: string; date_to?: string }) => {
    const p = new URLSearchParams();
    p.append("type", params.type);
    p.append("period", params.period);
    if (params.date_from) p.append("date_from", params.date_from);
    if (params.date_to) p.append("date_to", params.date_to);
    return `/api/v1/admin/reports/export/pdf?${p.toString()}`;
  },
};

// ─────────────────────────────────────────── Special Offer (Volume 15)
export const specialOfferApi = {
  topUsers: (params: { period: ReportPeriod; limit?: number }) =>
    get<TopUsersReport>("/admin/special-offers/top-users", params),
  list: (params?: { status?: string }) =>
    get<SpecialOffer[]>("/admin/special-offers", params),
  create: (payload: {
    user_id: string; total_reward: string; duration_days: number; note?: string;
  }) => post<SpecialOffer>(
    "/admin/special-offers/create", payload,
    { idempotencyKey: newIdempotencyKey() },
  ),
  detail: (id: number) => get<SpecialOffer>(`/admin/special-offers/${id}`),
  payouts: (offerId: number) =>
    get<SpecialOfferPayout[]>(`/admin/special-offers/${offerId}/payouts`),
  pause: (offerId: number) =>
    post<SpecialOffer>(`/admin/special-offers/${offerId}/pause`, {},
                          { idempotencyKey: newIdempotencyKey() }),
  resume: (offerId: number) =>
    post<SpecialOffer>(`/admin/special-offers/${offerId}/resume`, {},
                          { idempotencyKey: newIdempotencyKey() }),
  cancel: (offerId: number) =>
    post<SpecialOffer>(`/admin/special-offers/${offerId}/cancel`, {},
                          { idempotencyKey: newIdempotencyKey() }),
  triggerPayout: (offerId: number, payoutId: number) =>
    post<{ outcome: string }>(
      `/admin/special-offers/${offerId}/payouts/${payoutId}/process`, {},
      { idempotencyKey: newIdempotencyKey() },
    ),
};

// ─────────────────────────────────────────── Storefront & Dashboard (NEW)
export const storefrontApi = {
  publicData: () => get<StorefrontData>("/public/storefront"),
  dashboardStats: () => get<AdvancedDashboardStats>("/customer/advanced-stats"),

  admin: {
    sliders: () => get<HeroSliderItem[]>("/admin/storefront/banners"),
    createSlider: (data: any) => post<HeroSliderItem>("/admin/storefront/banners", data, { 
      idempotencyKey: newIdempotencyKey(),
      headers: { "Content-Type": "multipart/form-data" } // 🛠️ REQUIRED FOR IMAGES
    }),
    updateSlider: (id: number, data: any) => patch<HeroSliderItem>(`/admin/storefront/banners/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" } // 🛠️ REQUIRED FOR IMAGES
    }),
    deleteSlider: (id: number) => del<{ success: boolean }>(`/admin/storefront/banners/${id}`),
    
    sections: () => get<HomepageSection[]>("/admin/storefront/sections"),
    createSection: (data: any) => post<HomepageSection>("/admin/storefront/sections", data, { 
      idempotencyKey: newIdempotencyKey(),
      headers: { "Content-Type": "multipart/form-data" } // 🛠️ REQUIRED FOR IMAGES
    }),
    updateSection: (id: number, data: any) => patch<HomepageSection>(`/admin/storefront/sections/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" } // 🛠️ REQUIRED FOR IMAGES
    }),
    deleteSection: (id: number) => del<{ success: boolean }>(`/admin/storefront/sections/${id}`),
  }
};